import { useEffect } from "react";

export interface ShortcutMap {
  [combo: string]: () => void;
}

/**
 * Bind a map of `mod+key` keyboard combos. `mod` = Cmd on Mac, Ctrl elsewhere.
 * Combos are case-insensitive. Use `shift+mod+z` for redo, etc.
 * Skips when focus is on an input/textarea/select.
 */
export function useKeyboardShortcuts(map: ShortcutMap) {
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const tgt = e.target as HTMLElement | null;
      if (tgt && (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA" || tgt.isContentEditable)) {
        // Allow shortcuts that don't start with a printable key while typing (e.g. Esc)
        if (!e.metaKey && !e.ctrlKey && e.key !== "Escape") return;
      }
      const mod = e.metaKey || e.ctrlKey;
      const shift = e.shiftKey;
      const key = e.key.toLowerCase();
      const combo = [
        mod && "mod",
        shift && "shift",
        key === " " ? "space" : key,
      ].filter(Boolean).join("+");
      const fn = map[combo];
      if (fn) {
        e.preventDefault();
        fn();
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [map]);
}
